import React from 'react';
import * as translator from '../../../utils/translator';
import visaLogo from '../../../images/Payment Methods/visa.png';
import successBadge from '../../../images/repay/payment-success-badge.png';
import RepayButton from './RepayButton';

interface RepaySuccessStateProps {
  language: string;
  partNumber: string;
  paymentDate: string;
  amountPaid: string;
  maskedCardNumber: string;
  onDownloadConfirmation: () => void;
  onBackToHomepage: () => void;
}

const DocumentIcon: React.FC = () => (
  <svg width="14" height="18" viewBox="0 0 14 18" fill="none" xmlns="http://www.w3.org/2000/svg">
    <path d="M8.5 1H2.5A1.5 1.5 0 0 0 1 2.5v13A1.5 1.5 0 0 0 2.5 17h9a1.5 1.5 0 0 0 1.5-1.5V6L8.5 1Z" stroke="currentColor" strokeWidth="1.3" strokeLinejoin="round" />
    <path d="M8.5 1v4.5A1.5 1.5 0 0 0 10 7h3" stroke="currentColor" strokeWidth="1.3" strokeLinejoin="round" />
  </svg>
);

const ArrowLeftIcon: React.FC = () => (
  <svg width="16" height="12" viewBox="0 0 16 12" fill="none" xmlns="http://www.w3.org/2000/svg">
    <path d="M6 1 1 6l5 5M1 6h14" stroke="currentColor" strokeWidth="1.3" strokeLinecap="round" strokeLinejoin="round" />
  </svg>
);

const RepaySuccessState: React.FC<RepaySuccessStateProps> = ({
  language,
  partNumber,
  paymentDate,
  amountPaid,
  maskedCardNumber,
  onDownloadConfirmation,
  onBackToHomepage,
}) => {
  return (
    <div data-testid="repay-success-state" className="flex flex-col items-center py-[60px] px-[16px]">
      <img src={successBadge} alt="" className="w-[120px] h-auto mb-[16px]" />
      <h1 className="font-poppins font-bold text-[#0d2c54] text-[24px] md:text-[32px] text-center mb-[8px]">
        {translator.translate(language, 'repay', 'success/title')}
      </h1>
      <p className="font-poppins text-[#656d79] text-[14px] text-center mb-[24px]">
        {translator.translate(language, 'repay', 'success/subtitle')}
      </p>

      <div className="w-full max-w-[540px] bg-white border border-[#269c6e] rounded-[6px] p-[24px] flex flex-col gap-[24px]">
        <p className="font-poppins text-[#9d9d9d] text-[12px] text-center">
          {translator.translate(language, 'repay', 'success/card_message')}
        </p>

        <div className="flex flex-col gap-[16px]">
          <div className="flex items-center justify-between text-[14px] font-poppins">
            <span className="text-[#98a0af]">{translator.translate(language, 'repay', 'success/part_number')}</span>
            <span className="text-[#656d79] font-medium underline">{partNumber}</span>
          </div>
          <div className="flex items-center justify-between text-[14px] font-poppins">
            <span className="text-[#98a0af]">{translator.translate(language, 'repay', 'success/payment_date')}</span>
            <span className="text-[#656d79] font-medium">{paymentDate}</span>
          </div>
          <div className="flex items-center justify-between text-[14px] font-poppins">
            <span className="text-[#98a0af]">{translator.translate(language, 'repay', 'success/amount_paid')}</span>
            <span className="text-[#656d79] font-semibold">{amountPaid}</span>
          </div>
          <div className="flex items-center justify-between text-[14px] font-poppins">
            <span className="text-[#98a0af]">{translator.translate(language, 'repay', 'success/payment_method')}</span>
            <span className="flex items-center gap-[8px] text-[#656d79] font-medium">
              <img src={visaLogo} alt="Visa" className="h-[9px] w-auto" />
              {maskedCardNumber}
            </span>
          </div>
        </div>

        <ul className="list-disc pl-[16px] bg-[#f5f7f5] rounded-[6px] px-[24px] py-[16px] text-[#4d4d4d] text-[12px] font-poppins">
          <li className="mb-[8px]">{translator.translate(language, 'repay', 'success/info_bullet_1')}</li>
          <li>{translator.translate(language, 'repay', 'success/info_bullet_2')}</li>
        </ul>

        <div className="flex flex-col gap-[12px]">
          <RepayButton variant="solid" icon={<DocumentIcon />} onClick={onDownloadConfirmation}>
            {translator.translate(language, 'repay', 'success/download_confirmation')}
          </RepayButton>
          <RepayButton variant="outline" icon={<ArrowLeftIcon />} onClick={onBackToHomepage}>
            {translator.translate(language, 'repay', 'success/back_to_homepage')}
          </RepayButton>
        </div>
      </div>
    </div>
  );
};

export default RepaySuccessState;
